import React, { useState, useEffect, useRef } from 'react';
import { Share2, Calendar, MessageCircle, Swords, MapPin, Flame, Heart, Trophy, Star, Download } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const MILESTONE_ICONS = {
  calendar: Calendar,
  message: MessageCircle,
  swords: Swords,
  'map-pin': MapPin,
  flame: Flame,
};

const GrowthStory = () => {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    apiClient.request('/api/self-dev/growth-story')
      .then(setStory)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleShare = async () => {
    if (!navigator.share) {
      try {
        await navigator.clipboard.writeText(
          `My HiBFF Growth Story: ${story.stats.conversations} conversations, ${story.stats.challenges_completed} challenges completed, ${story.days_on_platform} days of growth! #HiBFF #SocialConfidence`
        );
        alert('Copied to clipboard!');
      } catch {}
      return;
    }
    try {
      await navigator.share({
        title: 'My HiBFF Growth Story',
        text: `${story.stats.conversations} conversations, ${story.stats.challenges_completed} challenges, ${story.days_on_platform} days of growth! #HiBFF`,
        url: window.location.origin,
      });
    } catch {}
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading your growth story...</div></DashboardLayout>;
  if (!story) return <DashboardLayout><div className="p-8 text-center text-gray-400">Could not load growth story</div></DashboardLayout>;

  const scoreGrowthColor = story.scores.growth > 0 ? 'text-green-600' : story.scores.growth < 0 ? 'text-red-500' : 'text-gray-500';

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="growth-story-page">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center justify-center gap-2">
            <Trophy size={28} className="text-amber-500" />
            My Growth Story
          </h1>
          <p className="text-sm text-gray-500 mt-1">Your journey from practice to real confidence</p>
        </div>

        {/* Story Card */}
        <div ref={cardRef} className="rounded-3xl overflow-hidden shadow-xl" data-testid="growth-card">
          {/* Top gradient */}
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-6 sm:p-8 text-white text-center">
            <p className="text-xs uppercase tracking-widest opacity-75 mb-2">HiBFF Growth Story</p>
            <h2 className="text-2xl sm:text-3xl font-black mb-1">{story.name}</h2>
            <p className="text-sm opacity-80 italic">&ldquo;{story.tagline}&rdquo;</p>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm opacity-75">
              <Calendar size={14} />
              <span>Member since {story.joined} ({story.days_on_platform} days)</span>
            </div>
          </div>

          {/* Before & After Score */}
          {story.scores.first_score > 0 && (
            <div className="bg-white p-5 sm:p-6 border-b border-gray-100" data-testid="score-comparison">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 text-center mb-3">Social Score Growth</p>
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-1">First Session</p>
                  <p className="text-3xl font-black text-gray-300">{story.scores.first_score}</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className={`text-lg font-black ${scoreGrowthColor}`}>
                    {story.scores.growth > 0 ? '+' : ''}{story.scores.growth}
                  </div>
                  <p className="text-[10px] text-gray-400">points growth</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-1">Latest Session</p>
                  <p className="text-3xl font-black text-indigo-600">{story.scores.latest_score}</p>
                </div>
              </div>
              {story.scores.growth_percent > 0 && (
                <p className="text-center text-sm text-green-600 font-semibold mt-2">
                  {story.scores.growth_percent}% improvement
                </p>
              )}
            </div>
          )}

          {/* Stats Grid */}
          <div className="bg-white p-5 sm:p-6 border-b border-gray-100">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBox icon={MessageCircle} color="text-blue-500" bg="bg-blue-50" value={story.stats.conversations} label="Conversations" />
              <StatBox icon={Swords} color="text-indigo-500" bg="bg-indigo-50" value={story.stats.challenges_completed} label="Challenges" />
              <StatBox icon={MapPin} color="text-teal-500" bg="bg-teal-50" value={story.stats.meetups_completed} label="Real Meetups" />
              <StatBox icon={Flame} color="text-orange-500" bg="bg-orange-50" value={story.stats.longest_streak} label="Best Streak" />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <StatBox icon={Star} color="text-amber-500" bg="bg-amber-50" value={story.stats.quests_completed} label="Quests" />
              <StatBox icon={Heart} color="text-pink-500" bg="bg-pink-50" value={story.stats.hearts_earned} label="Hearts" />
              <StatBox icon={Trophy} color="text-purple-500" bg="bg-purple-50" value={story.stats.dares_completed} label="Dares" />
            </div>
          </div>

          {/* Milestones */}
          {story.milestones.length > 0 && (
            <div className="bg-white p-5 sm:p-6">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-3">Milestones Unlocked</p>
              <div className="flex flex-wrap gap-2">
                {story.milestones.map((m, i) => {
                  const Icon = MILESTONE_ICONS[m.icon] || Star;
                  return (
                    <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-full" data-testid={`milestone-${i}`}>
                      <Icon size={12} className="text-amber-500" />
                      <span className="text-xs font-semibold text-amber-800">{m.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Share Button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
            data-testid="share-growth-btn"
          >
            <Share2 size={16} />
            Share My Story
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

const StatBox = ({ icon: Icon, color, bg, value, label }) => (
  <div className={`${bg} rounded-xl p-3 text-center`}>
    <Icon size={16} className={`${color} mx-auto mb-1`} />
    <p className="text-xl font-black text-gray-900">{value}</p>
    <p className="text-[9px] text-gray-500 uppercase tracking-wider">{label}</p>
  </div>
);

export default GrowthStory;
